import { AutomationValue } from './AutomationValue.js';

function cloneStructured(value) {
  if (Array.isArray(value)) return value.map(cloneStructured);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, cloneStructured(item)]));
  }
  return value;
}

/**
 * AutomationAction
 *
 * A platform-independent action model.
 * Does not reference Blockly or Home Assistant.
 *
 * provider + service together identify what to call:
 *   provider='light'         + service='toggle'    → light.toggle
 *   provider='esphome'       + service='led_rainbow' → esphome.led_rainbow
 *   provider='openhasp'      + service='command'   → openhasp.command
 *   provider='homeassistant' + service='toggle'    → homeassistant.toggle
 *
 * All provider-specific arguments live in parameters — including target
 * identifiers (e.g. key='entity_id'). Generators decide how to route each
 * parameter key to their target format.
 *
 * New providers never require AST changes — only new generators.
 */
export class AutomationAction {
  /**
   * @param {object} config
   * @param {string} [config.id]
   * @param {string} [config.type]       — 'call_service' | 'delay' | ...
   * @param {string} [config.provider]   — integration / service domain
   * @param {string} [config.service]    — service name within the provider
   * @param {object|null} [config.target] — provider-native target object
   * @param {object|null} [config.data]   — recursively structured action data
   * @param {object|null} [config.extraFields] — unrecognized HA action keys
   * @param {object|null} [config.editorMetadata] — persisted, never exported
   * @param {Array<{key: string, source?: 'object'|'literal'|'event', value: AutomationValue}>} [config.parameters]
   *   All arguments, including target identifiers.
   *   Convention: key='entity_id' carries the target entity or entity list.
   */
  constructor({
    id,
    type       = 'call_service',
    provider   = '',
    service    = '',
    target     = null,
    data       = null,
    extraFields = null,
    editorMetadata = null,
    parameters = [],
  } = {}) {
    this.id       = id ?? crypto.randomUUID();
    this.type     = type;
    this.provider = provider;
    this.service  = service;
    this.target   = target == null ? null : cloneStructured(target);
    this.data     = data == null ? null : cloneStructured(data);
    this.extraFields = extraFields == null ? null : cloneStructured(extraFields);
    this.editorMetadata = editorMetadata == null ? null : cloneStructured(editorMetadata);

    // Ensure every parameter value is an AutomationValue instance
    this.parameters = parameters.map(p => {
      if (p.value instanceof AutomationValue) return p;
      const v = p.value;
      const parameter = {
        key:   p.key,
        value: v ? AutomationValue.fromJSON(v) : AutomationValue.literal(''),
      };
      if (p.source != null) parameter.source = p.source;
      return parameter;
    });
  }

  toJSON() {
    const json = {
      id:         this.id,
      type:       this.type,
      provider:   this.provider,
      service:    this.service,
      target:     this.target == null ? null : cloneStructured(this.target),
      data:       this.data == null ? null : cloneStructured(this.data),
      extraFields: this.extraFields == null ? null : cloneStructured(this.extraFields),
      editorMetadata: this.editorMetadata == null ? null : cloneStructured(this.editorMetadata),
      parameters: this.parameters.map(p => ({
        key: p.key,
        ...(p.source != null ? { source: p.source } : {}),
        value: p.value.toJSON(),
      })),
    };
    if (this.provider === undefined || (!this.provider && String(this.service).includes('.'))) {
      delete json.provider;
    }
    return json;
  }

  static fromJSON(data) {
    if (!data) return null;
    return new AutomationAction({
      ...data,
      parameters: (data.parameters ?? []).map(p => ({
        key:   p.key,
        ...(p.source != null ? { source: p.source } : {}),
        value: AutomationValue.fromJSON(p.value),
      })),
    });
  }
}
